import { AccountInfo } from '../models/account';
import { AccountBindResponse, EmailIdentityRequest, IdentityLoginToken, JwtToken, VerifyIdentityRequest } from '../models/auth';
import { LinkInfo } from "../models/link";
import { InvoiceInfo, PayHistoryInfo, PayType } from "../models/payment";
import { PlatformCard } from "../models/platform";
import { ResultResponse } from "../models/result";
import { ServerInfo } from "../models/servers";
import { CheckSubscriptionResult, Subscription } from "../models/subscriptions";
import { TelegramProxyLink } from "../models/tgProxy";

import type { IRestClient } from './RestClientContext';

export class SubscriptionNotFoundError extends Error { }

const MOCK_DELAY = 1200; // Имитируем задержку сети

const delay = (ms: number = MOCK_DELAY) => new Promise(resolve => setTimeout(resolve, ms));

export class MockRestClient implements IRestClient {
    readonly baseUrl: string;
    readonly initData: string;
    private isSubscribed: boolean;

    bindEmail = async (request: EmailIdentityRequest): Promise<ResultResponse<AccountBindResponse>> => ({ success: true, result: { rawToken: 'mock-bind-token', tokenExpireTimeMinutes: 10 }, error: '', message: 'Код отправлен' });
    verifyEmailBinding = async (request: VerifyIdentityRequest): Promise<ResultResponse<void>> => ({ success: true, result: undefined, error: '', message: 'Email привязан' });
    loginByEmail = async (request: EmailIdentityRequest): Promise<IdentityLoginToken> => ({ hash: 'mock-login-token', duration: 'PT10M' });
    verifyEmailLogin = async (request: VerifyIdentityRequest): Promise<JwtToken> => ({ token: 'mock-jwt-token', duration: 'PT1H' });
    getAccountInfo = async (): Promise<ResultResponse<AccountInfo>> => ({
        success: true,
        result: {
            tgName: 'mock-user',
            tgId: 123456,
            identities: [
                { identity: 'mock@example.com', type: 'EMAIL' },
            ],
        },
        error: '',
        message: 'OK',
    });

    constructor(baseUrl: string, initData: string) {
        this.baseUrl = baseUrl;
        this.initData = initData;
        this.isSubscribed = false;
    }

    getServersInternal = async (): Promise<ServerInfo[]> => {
        await delay();
        return [
            {
                name: 'mock-server-1',
                ping: 100,
                isActive: true

            },
            {
                name: 'mock-server-2',
                ping: 150,
                isActive: false
            }]
    }

    checkSubscription = async (): Promise<CheckSubscriptionResult> => {
        await delay();
        return this.isSubscribed ?
            {
                hasSubscription: true,
                obj: {
                    id: 'mock-sub-1',
                    type: 'PAYED',
                    daysLeft: 30,
                    usedGb: 5.5,
                    hours: 120
                }
            } :
            {
                hasSubscription: false
            };
    }

    createSubscription = async (ref?: string): Promise<ResultResponse<Subscription>> => {
        await delay();
        this.isSubscribed = true;
        return {
            result: {
                id: 'mock-sub-new',
                type: 'FREE',
                daysLeft: 7,
                usedGb: 0,
                hours: 0
            },
            success: true,
            error: '',
            message: 'Подписка создана'
        };
    }

    getPlatforms = async (): Promise<ResultResponse<PlatformCard[]>> => {
        await delay();
        return {
            error: "",
            message: "",
            success: true,
            result: [
                {
                    "id": "ios",
                    "platform": "iOS",
                    "match": ["ios", "iphone", "ipad"],
                    "iconName": "apple-logo",
                    "providers": [
                        {
                            "appName": "Happ",
                            "steps": [
                                {
                                    "type": "DownloadApp",
                                    "appName": "Happ",
                                    "storeName": "App Store",
                                    "link": "https://apps.apple.com/ru/app/happ-proxy-utility-plus/id6746188973",
                                    "img": "https://play-lh.googleusercontent.com/a3dR56g1viLgbtdBBu_NSEdDuWkqJFIn5XFFoc7lKXApkf-av4HI7fTn2NhBjxVmPFHf=w240-h480-rw"
                                },
                                {
                                    "type": "CopyLink",
                                    "linkTransform": { type: "happcrypto" },
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Откройте Happ клиент и вставьте ссылку как указано на картинке: ",
                                    "img": "https://github.com/owpk/ogo-sa-blobs/blob/main/happ_copy_link.jpg?raw=true"
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Выберите локацию и подключитесь",
                                    "img": "https://github.com/owpk/ogo-sa-blobs/blob/main/happ_launch.jpg?raw=true"
                                }
                            ]
                        },
                        {
                            "appName": "V2RayTun",
                            "videoInstruction": "v2raytune_instruction.webm",
                            "steps": [
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "⚠️ Внимание! По требованию Роскомнадзора App Store удалил большинство клиентов, вы можете сменить регион в настройках App Store и приложения снова будут доступны, инструкцию легко можно найти в интернете"
                                },
                                {
                                    "type": "DownloadApp",
                                    "appName": "V2RayTun",
                                    "storeName": "App Store",
                                    "link": "https://apps.apple.com/ca/app/v2raytun/id6476628951",
                                    "img": "https://play-lh.googleusercontent.com/LZ4jXc1dvJzi0VabcAIMXBuW3X3MQUO2lDToAQL4sNZHTqWbBokgbeog-5bKjJFKZcc=w480-h960-rw"
                                },
                                {
                                    "type": "ImportLink",
                                    "importUri": "v2raytun://import/",
                                    "fallbackCopyInstruction": [
                                        {
                                            "type": "CopyLink"
                                        },
                                        {
                                            "type": "LaunchInstruction",
                                            "instruction": "Откройте V2RayTun -> нажмите иконку '+' сверху справа → 'Добавить из буфера'",
                                            "img": "https://github.com/owpk/ogo-sa-blobs/blob/main/v2raytun_copy_link.jpg?raw=true"
                                        }
                                    ]
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Выберите локацию и нажмите 'Подключиться' (Синяя кнопка по середине сверху)",
                                    "img": "https://github.com/owpk/ogo-sa-blobs/blob/be41a113b930870b3f2c93e0b0877ab7273b1a6e/v2raytun_launch.jpg?raw=true"
                                }
                            ]
                        },
                        {
                            "appName": "Streisand",
                            "steps": [
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "⚠️ Внимание! По требованию Роскомнадзора App Store удалил большинство клиентов, вы можете сменить регион в настройках App Store и приложения снова будут доступны, инструкцию легко можно найти в интернете"
                                },
                                {
                                    "type": "DownloadApp",
                                    "appName": "Streisand",
                                    "storeName": "App Store",
                                    "link": "https://apps.apple.com/ca/app/streisand/id6450534064"
                                },
                                {
                                    "type": "ImportLink",
                                    "importUri": "streisand://import/",
                                    "fallbackCopyInstruction": [
                                        {
                                            "type": "CopyLink"
                                        },
                                        {
                                            "type": "LaunchInstruction",
                                            "instruction": "Откройте приложение, нажмите на иконку '+' в верхнем правом углу и импортируйте конфигурацию из буфера обмена (Добавить из буфера)"
                                        }
                                    ]
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Выберите сервер и подключитесь"
                                }
                            ]
                        }
                    ]
                },
                {
                    "id": "android",
                    "platform": "Android",
                    "match": ["android"],
                    "iconName": "android-logo",
                    "providers": [
                        {
                            "appName": "V2RayTun",
                            "videoInstruction": "v2raytune_instruction.webm",
                            "steps": [
                                {
                                    "type": "DownloadApp",
                                    "appName": "V2RayTun",
                                    "storeName": "Google Play",
                                    "link": "https://play.google.com/store/apps/details?id=com.v2raytun.android",
                                    "img": "https://play-lh.googleusercontent.com/LZ4jXc1dvJzi0VabcAIMXBuW3X3MQUO2lDToAQL4sNZHTqWbBokgbeog-5bKjJFKZcc=w480-h960-rw"
                                },
                                {
                                    "type": "ImportLink",
                                    "importUri": "v2raytun://import/",
                                    "fallbackCopyInstruction": [
                                        {
                                            "type": "CopyLink"
                                        },
                                        {
                                            "type": "LaunchInstruction",
                                            "instruction": "Откройте V2RayTun -> нажмите иконку '+' сверху справа → 'Добавить из буфера'",
                                            "img": "https://github.com/owpk/ogo-sa-blobs/blob/main/v2raytun_copy_link.jpg?raw=true"
                                        }
                                    ]
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Выберите локацию и нажмите 'Подключиться' (Синяя кнопка по середине сверху)",
                                    "img": "https://github.com/owpk/ogo-sa-blobs/blob/be41a113b930870b3f2c93e0b0877ab7273b1a6e/v2raytun_launch.jpg?raw=true"
                                }
                            ]
                        },
                        {
                            "appName": "Hiddify",
                            "videoInstruction": "hiddify_instruction_manual.webm",
                            "steps": [
                                {
                                    "type": "DownloadApp",
                                    "appName": "Hiddify",
                                    "storeName": "Play Market",
                                    "link": "https://play.google.com/store/apps/details?id=app.hiddify.com"
                                },
                                {
                                    "type": "ImportLink",
                                    "importUri": "hiddify://import/",
                                    "fallbackCopyInstruction": [
                                        {
                                            "type": "CopyLink"
                                        },
                                        {
                                            "type": "LaunchInstruction",
                                            "instruction": "Откройте приложение, нажмите на иконку '+' (новый профиль) в верхнем правом углу и импортируйте конфигурацию из буфера обмена (Добавить из буфера)"
                                        }
                                    ]
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Выберите сервер и подключитесь"
                                }
                            ]
                        }
                    ]
                },
                {
                    "id": "mac",
                    "platform": "MacOS",
                    "match": ["mac"],
                    "iconName": "apple-logo",
                    "providers": [
                        {
                            "appName": "Happ",
                            "steps": [
                                {
                                    "type": "DownloadApp",
                                    "appName": "Happ",
                                    "storeName": "App Store",
                                    "link": "https://apps.apple.com/ru/app/happ-proxy-utility-plus/id6746188973",
                                    "img": "https://play-lh.googleusercontent.com/a3dR56g1viLgbtdBBu_NSEdDuWkqJFIn5XFFoc7lKXApkf-av4HI7fTn2NhBjxVmPFHf=w240-h480-rw"
                                },
                                {
                                    "type": "ImportLink",
                                    "linkTransform": {
                                        "type": "happcrypto"
                                    },
                                    "fallbackCopyInstruction": [
                                        {
                                            "type": "CopyLink",
                                            "linkTransform": {
                                                "type": "happcrypto"
                                            },
                                        },
                                        {
                                            "type": "LaunchInstruction",
                                            "instruction": "Откройте Happ → нажмите '+' → вставьте ссылку из буфера обмена",
                                            "img": "https://github.com/owpk/ogo-sa-blobs/blob/main/happ_copy_link.jpg?raw=true"
                                        }
                                    ]
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Выберите локацию и подключитесь",
                                    "img": "https://github.com/owpk/ogo-sa-blobs/blob/main/happ_launch.jpg?raw=true"
                                }
                            ]
                        },
                        {
                            "appName": "V2RayTun",
                            "videoInstruction": "v2raytune_instruction.webm",
                            "steps": [
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "⚠️ Внимание! По требованию Роскомнадзора App Store удалил большинство клиентов, вы можете сменить регион в настройках App Store и приложения снова будут доступны, инструкцию легко можно найти в интернете"
                                },
                                {
                                    "type": "DownloadApp",
                                    "appName": "V2RayTun",
                                    "storeName": "App Store",
                                    "link": "https://apps.apple.com/ca/app/v2raytun/id6476628951",
                                    "img": "https://play-lh.googleusercontent.com/LZ4jXc1dvJzi0VabcAIMXBuW3X3MQUO2lDToAQL4sNZHTqWbBokgbeog-5bKjJFKZcc=w480-h960-rw"
                                },
                                {
                                    "type": "ImportLink",
                                    "importUri": "v2raytun://import/",
                                    "fallbackCopyInstruction": [
                                        {
                                            "type": "CopyLink",
                                            "img": "https://github.com/owpk/ogo-sa-blobs/blob/main/happ_copy_link.jpg?raw=true"
                                        },
                                        {
                                            "type": "LaunchInstruction",
                                            "instruction": "Откройте V2RayTun -> нажмите иконку '+' сверху справа → 'Добавить из буфера'",
                                            "img": "https://github.com/owpk/ogo-sa-blobs/blob/main/v2raytun_copy_link.jpg?raw=true"
                                        }
                                    ]
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Выберите локацию и нажмите 'Подключиться' (Синяя кнопка по середине сверху)",
                                    "img": "https://github.com/owpk/ogo-sa-blobs/blob/be41a113b930870b3f2c93e0b0877ab7273b1a6e/v2raytun_launch.jpg?raw=true"
                                }
                            ]
                        },
                        {
                            "appName": "Hiddify",
                            "videoInstruction": "hiddify_instruction_manual.webm",
                            "steps": [
                                {
                                    "type": "DownloadApp",
                                    "appName": "Hiddify",
                                    "storeName": "GitHub",
                                    "link": "https://github.com/hiddify/hiddify-next/releases/latest/download/Hiddify-MacOS.dmg"
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Запустите приложение, система выдаст предупреждение 'Файл «Hiddify» не был открыт'"
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Перейдите в настройки системы -> Конфиденциальность и безопасность. В самом низу, в разделе 'Безопасность' разрешите использование Hiddify"
                                },
                                {
                                    "type": "ImportLink",
                                    "importUri": "hiddify://import/",
                                    "fallbackCopyInstruction": [
                                        {
                                            "type": "CopyLink"
                                        },
                                        {
                                            "type": "LaunchInstruction",
                                            "instruction": "Откройте приложение, нажмите на иконку '+' (новый профиль) в верхнем правом углу и импортируйте конфигурацию из буфера обмена (Добавить из буфера)"
                                        }
                                    ]
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Выберите сервер и подключитесь"
                                }
                            ]
                        }
                    ]
                },
                {
                    "id": "windows",
                    "platform": "Windows",
                    "match": ["windows"],
                    "iconName": "windows-logo",
                    "providers": [
                        {
                            "appName": "V2RayTun",
                            "videoInstruction": "v2raytune_instruction.webm",
                            "steps": [
                                {
                                    "type": "DownloadApp",
                                    "appName": "V2RayTun",
                                    "storeName": "GitHub",
                                    "link": "https://github.com/mdf45/v2raytun/releases/download/v3.7.10/v2RayTun_Setup.exe"
                                },
                                {
                                    "type": "CopyLink"
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Откройте V2RayTun -> нажмите иконку '+' сверху справа → 'Добавить из буфера'",
                                    "img": "https://github.com/owpk/ogo-sa-blobs/blob/main/v2raytun_copy_link.jpg?raw=true"
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Выберите локацию и нажмите 'Подключиться' (Синяя кнопка по середине сверху)",
                                    "img": "https://github.com/owpk/ogo-sa-blobs/blob/be41a113b930870b3f2c93e0b0877ab7273b1a6e/v2raytun_launch.jpg?raw=true"
                                }
                            ]
                        },
                        {
                            "appName": "Hiddify",
                            "videoInstruction": "hiddify_instruction_manual.webm",
                            "steps": [
                                {
                                    "type": "DownloadApp",
                                    "appName": "Hiddify",
                                    "storeName": "GitHub",
                                    "link": "https://github.com/hiddify/hiddify-next/releases/latest/download/Hiddify-Windows-Setup-x64.exe"
                                },
                                {
                                    "type": "CopyLink"
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Откройте приложение, нажмите на иконку '+' (новый профиль) в верхнем правом углу и импортируйте конфигурацию из буфера обмена (Добавить из буфера)"
                                },
                                {
                                    "type": "LaunchInstruction",
                                    "instruction": "Выберите сервер и подключитесь"
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    }

    getPayTypes = async (): Promise<PayType[]> => {
        await delay();
        return [
            {
                id: 'monthly',
                friendlyName: 'Месячная подписка',
                price: 299,
                daysDuration: 30
            },
            {
                id: 'quarterly',
                friendlyName: 'Квартальная подписка',
                price: 799,
                daysDuration: 90
            }
        ];
    }

    termOfUse = async (): Promise<ResultResponse<string>> => {
        await delay();
        return {
            result: 'Условия использования. Lorem ipsum dolor sit amet...',
            success: true,
            error: '',
            message: 'Условия получены'
        };
    }

    getServers = async (): Promise<ServerInfo[]> => {
        await delay();
        return [
            {
                name: 'Server 1 - Moscow',
                ping: 15,
                isActive: true
            },
            {
                name: 'Server 2 - Amsterdam',
                ping: 45,
                isActive: true
            },
            {
                name: 'Server 3 - New York',
                ping: 120,
                isActive: false
            }
        ];
    }

    getLink = async (): Promise<LinkInfo> => {
        await delay();
        return {
            link: 'ovpn://import-profile/?profile_url=https://example.com/profile.ovpn'
        };
    }

    getTelegramProxyLinks = async (): Promise<TelegramProxyLink[]> => {
        await delay();
        return [
            {
                id: 1,
                friendlyName: 'Основной MTProxy',
                url: 'https://t.me/proxy?server=185.94.167.114&port=443&secret=00000000000000000000000000000000'
            },
            {
                id: 2,
                friendlyName: 'Резервный MTProxy',
                url: 'tg://proxy?server=185.94.167.115&port=443&secret=11111111111111111111111111111111'
            }
        ];
    }

    getPayHistory = async (page: number, size: number, type: string[]): Promise<PayHistoryInfo[]> => {
        await delay();
        return [
            {
                internalPaymentId: 'pay-1',
                amount: 299,
                status: 'SUCCESS',
                createdAt: '2026-01-28T10:30:00Z'
            },
            {
                internalPaymentId: 'pay-2',
                amount: 799,
                status: 'SUCCESS',
                createdAt: '2025-12-28T10:30:00Z'
            }
        ];
    }

    createInvoice = async (type: string): Promise<InvoiceInfo> => {
        await delay();
        return {
            paymentId: 'mock-payment-' + Date.now(),
            walletId: 'mock-wallet-123',
            status: 'NEW'
        };
    }

    fetchTgPaymentLink = async (type: string): Promise<LinkInfo> => {
        await delay();
        return {
            link: 'https://t.me/payments?startapp=mock-payment-' + Date.now()
        };
    }

    getReferralHash = async (): Promise<ResultResponse<string>> => {
        await delay();
        return {
            result: 'mock-referral-hash-abc123',
            success: true,
            error: '',
            message: 'Реферальная ссылка получена'
        };
    }

    getReferralsCount = async (): Promise<ResultResponse<number>> => {
        await delay();
        return {
            result: 5,
            success: true,
            error: '',
            message: 'Количество рефералов получено'
        };
    }

    happCrypto = async (url: string): Promise<ResultResponse<string>> => {
        await delay(800);
        return {
            result: `happ://mock-encrypted?url=${encodeURIComponent(url)}`,
            success: true,
            error: '',
            message: 'Ссылка зашифрована'
        };
    }
}
